import React, { useState, useEffect } from 'react';
import { X, Bell, CheckCheck, Trash2, ExternalLink, Clock } from 'lucide-react';
import { notificationsAPI, socialAPI } from '../services/api';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { tr } from 'date-fns/locale';

const NotificationDrawer = ({ isOpen, onClose, onUnreadUpdate }) => {
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(false);
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();

    const fetchNotifications = async () => {
        setLoading(true);
        try {
            const response = await notificationsAPI.list();
            setNotifications(response.data);
            updateUnreadCount(response.data);
        } catch (error) {
            console.error('Failed to fetch notifications:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isOpen) {
            fetchNotifications();
        }
    }, [isOpen]);

    const updateUnreadCount = (data) => {
        const unreadCount = data.filter(n => !n.read).length;
        if (onUnreadUpdate) onUnreadUpdate(unreadCount);
    };

    const handleMarkAsRead = async (id) => {
        try {
            await notificationsAPI.markRead(id);
            setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
            updateUnreadCount(notifications.map(n => n.id === id ? { ...n, read: true } : n));
        } catch (error) {
            console.error('Failed to mark notification as read:', error);
        }
    };

    const handleMarkAllRead = async () => {
        try {
            await notificationsAPI.markAllRead();
            setNotifications(prev => prev.map(n => ({ ...n, read: true })));
            if (onUnreadUpdate) onUnreadUpdate(0);
        } catch (error) {
            console.error('Failed to mark all as read:', error);
        }
    };

    const handleDelete = async (e, id) => {
        e.stopPropagation();
        try {
            await notificationsAPI.delete(id);
            setNotifications(prev => prev.filter(n => n.id !== id));
            updateUnreadCount(notifications.filter(n => n.id !== id));
        } catch (error) {
            console.error('Failed to delete notification:', error);
        }
    };

    const handleAcceptFollow = async (e, n) => {
        e.stopPropagation();
        try {
            const data = typeof n.data === 'string' ? JSON.parse(n.data) : n.data;
            if (!data?.follow_id) return;

            await socialAPI.acceptFollow(data.follow_id);
            // Update notification locally to show it was accepted or just mark as read
            setNotifications(prev => prev.map(item =>
                item.id === n.id ? { ...item, read: true, type: 'FOLLOW_ACCEPTED_LOCAL' } : item
            ));
        } catch (error) {
            console.error('Failed to accept follow:', error);
        }
    };

    const handleRejectFollow = async (e, n) => {
        e.stopPropagation();
        try {
            const data = typeof n.data === 'string' ? JSON.parse(n.data) : n.data;
            if (!data?.follow_id) return;

            await socialAPI.rejectFollow(data.follow_id);
            // Remove notification or update it
            setNotifications(prev => prev.filter(item => item.id !== n.id));
        } catch (error) {
            console.error('Failed to reject follow:', error);
        }
    };

    const handleNotificationClick = (n) => {
        if (!n.read) handleMarkAsRead(n.id);
        if (n.link) {
            navigate(n.link);
            onClose();
        }
    };

    const getRelativeTime = (date) => {
        try {
            return formatDistanceToNow(new Date(date), {
                addSuffix: true,
                locale: i18n.language === 'tr' ? tr : undefined
            });
        } catch (e) {
            return '';
        }
    };

    const handleAvatarOrNameClick = (e, n) => {
        e.stopPropagation();
        if (!n.sender_username) return;
        if (!n.read) handleMarkAsRead(n.id);
        navigate(`/profile/${n.sender_username}`);
        onClose();
    };

    const renderWithClickableName = (text, n) => {
        if (!text || (!n.sender_name && !n.sender_username)) return text;

        const lowerText = text.toLowerCase();
        let targetName = null;
        let index = -1;

        if (n.sender_username && lowerText.indexOf(n.sender_username.toLowerCase()) !== -1) {
            targetName = n.sender_username;
            index = lowerText.indexOf(n.sender_username.toLowerCase());
        } else if (n.sender_name && lowerText.indexOf(n.sender_name.toLowerCase()) !== -1) {
            targetName = n.sender_name;
            index = lowerText.indexOf(n.sender_name.toLowerCase());
        }

        if (index === -1) return text;

        const before = text.substring(0, index);
        const name = text.substring(index, index + targetName.length);
        const after = text.substring(index + targetName.length);

        return (
            <>
                {before}
                <span
                    onClick={(e) => handleAvatarOrNameClick(e, n)}
                    className="font-bold text-white hover:text-snapp-green hover:underline cursor-pointer transition-colors"
                >
                    {name}
                </span>
                {after}
            </>
        );
    };

    if (!isOpen) return null;

    return (
        <>
            {/* Overlay */}
            <div
                className="fixed inset-0 bg-black/50 z-[100] transition-opacity"
                onClick={onClose}
            />

            {/* Drawer */}
            <div className="fixed right-0 top-0 h-full w-full max-w-sm bg-[#1a1a1b] border-l border-gray-800 shadow-2xl z-[101] flex flex-col animate-in slide-in-from-right duration-300">
                {/* Header */}
                <div className="p-4 border-b border-gray-800 flex items-center justify-between bg-[#1a1a1b]">
                    <div className="flex items-center">
                        <Bell className="w-5 h-5 text-snapp-green mr-2" />
                        <h2 className="text-lg font-bold text-white">{t('notifications.title', 'Bildirimler')}</h2>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={handleMarkAllRead}
                            className="p-2 text-gray-400 hover:text-white transition-colors"
                            title={t('notifications.markAllRead', 'Hepsini okundu işaretle')}
                        >
                            <CheckCheck className="w-5 h-5" />
                        </button>
                        <button
                            onClick={onClose}
                            className="p-2 text-gray-400 hover:text-white transition-colors"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                {/* List */}
                <div className="flex-1 overflow-y-auto">
                    {loading && (
                        <div className="flex items-center justify-center p-8">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-snapp-green"></div>
                        </div>
                    )}

                    {!loading && notifications.length === 0 && (
                        <div className="flex flex-col items-center justify-center p-12 text-center">
                            <Bell className="w-12 h-12 text-gray-700 mb-4" />
                            <p className="text-gray-500">{t('notifications.empty', 'Henüz bildiriminiz yok.')}</p>
                        </div>
                    )}

                    {notifications.map((n) => (
                        <div
                            key={n.id}
                            onClick={() => handleNotificationClick(n)}
                            className={`p-4 border-b border-gray-800/50 cursor-pointer transition-colors group relative ${!n.read ? 'bg-snapp-green/5 hover:bg-snapp-green/10' : 'hover:bg-gray-800/50'
                                }`}
                        >
                            <div className="flex gap-3">
                                {/* Avatar / Icon */}
                                <div
                                    className={`flex-shrink-0 mt-1 ${n.sender_username ? 'cursor-pointer' : ''}`}
                                    onClick={(e) => n.sender_username && handleAvatarOrNameClick(e, n)}
                                >
                                    {n.sender_avatar ? (
                                        <img src={n.sender_avatar} alt="" className="w-10 h-10 rounded-full object-cover border border-transparent hover:border-snapp-green transition-colors" />
                                    ) : (
                                        <div className="w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center hover:bg-gray-700 transition-colors">
                                            <Bell className="w-5 h-5 text-gray-500" />
                                        </div>
                                    )}
                                </div>

                                {/* Content */}
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center justify-between mb-1">
                                        <span className="text-sm font-normal text-white truncate group-hover:text-gray-200">
                                            {renderWithClickableName(n.title, n)}
                                        </span>
                                        {!n.read && (
                                            <span className="w-2 h-2 rounded-full bg-snapp-green"></span>
                                        )}
                                    </div>
                                    <p className="text-sm text-gray-400 line-clamp-2 mb-1">
                                        {renderWithClickableName(n.message, n)}
                                    </p>
                                    <div className="flex items-center text-[10px] text-gray-500">
                                        <Clock className="w-3 h-3 mr-1" />
                                        {getRelativeTime(n.created_at)}
                                    </div>
                                </div>

                                {/* Actions */}
                                <div className="opacity-0 group-hover:opacity-100 transition-opacity flex flex-col gap-2">
                                    {n.type === 'FOLLOW_REQUEST' && !n.read && (
                                        <div className="flex gap-2 mb-2">
                                            <button
                                                onClick={(e) => handleAcceptFollow(e, n)}
                                                className="px-3 py-1 bg-snapp-green text-black text-xs font-bold rounded hover:bg-snapp-green/80 transition-colors"
                                            >
                                                {t('common.approve', 'Onayla')}
                                            </button>
                                            <button
                                                onClick={(e) => handleRejectFollow(e, n)}
                                                className="px-3 py-1 bg-gray-700 text-white text-xs font-bold rounded hover:bg-gray-600 transition-colors"
                                            >
                                                {t('common.reject', 'Reddet')}
                                            </button>
                                        </div>
                                    )}
                                    <button
                                        onClick={(e) => handleDelete(e, n.id)}
                                        className="p-1 text-gray-500 hover:text-red-500 transition-colors self-end"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </>
    );
};

export default NotificationDrawer;
